import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { importLogService } from '@/services/importLogService';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

/** Libelle lisible pour chaque type d'import connu, avec repli generique */
const TYPE_LABELS: Record<string, string> = {
  vacations: 'Vacations',
  agents: 'Agents',
};

/**
 * Rapport des imports de donnees (vacations, agents, ...): permet de retrouver
 * apres coup le resultat complet d'un import, les toasts affiches au moment de
 * l'import etant ephemeres et limites a 10 erreurs.
 */
export function ImportLogsPage() {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['import-logs', page, typeFilter],
    queryFn: () => importLogService.list(page, 20, typeFilter || undefined),
  });

  const formatDate = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement du rapport d'import...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <DocumentTextIcon className="w-6 h-6 mr-2" />
            Rapport des imports
          </h2>
          <p className="text-sm text-gray-500">
            Historique complet des imports de donnees (vacations, agents, ...)
          </p>
        </div>
        <select
          className="input w-48"
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Tous les types</option>
          <option value="vacations">Vacations</option>
          <option value="agents">Agents</option>
        </select>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Fichier</th>
                  <th>Utilisateur</th>
                  <th>Lignes</th>
                  <th>Importes</th>
                  <th>Ignores</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data?.data.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8 text-gray-500">
                      Aucun import realise pour l'instant
                    </td>
                  </tr>
                ) : (
                  data?.data.map((log) => {
                    const hasErreurs = (log.erreurs?.length ?? 0) > 0;
                    const isExpanded = expandedId === log.id;

                    return (
                      <Fragment key={log.id}>
                        <tr className={!log.succes ? 'bg-red-50' : ''}>
                          <td className="whitespace-nowrap text-gray-500">
                            <div className="flex items-center">
                              <ClockIcon className="w-4 h-4 mr-1.5 text-gray-400" />
                              {formatDate(log.dateImport)}
                            </div>
                          </td>
                          <td>
                            <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                              {TYPE_LABELS[log.type] || log.type}
                            </span>
                          </td>
                          <td className="max-w-[200px] truncate" title={log.nomFichier || ''}>
                            {log.nomFichier || '-'}
                          </td>
                          <td>
                            {log.utilisateur ? `${log.utilisateur.prenom} ${log.utilisateur.nom}` : '-'}
                          </td>
                          <td>{log.nombreLignes}</td>
                          <td className="text-green-600 font-medium">{log.nombreImportes}</td>
                          <td className={log.nombreIgnores > 0 ? 'text-amber-600 font-medium' : 'text-gray-400'}>
                            {log.nombreIgnores}
                          </td>
                          <td>
                            {log.succes ? (
                              <span className="inline-flex items-center text-green-700 text-sm">
                                <CheckCircleIcon className="w-4 h-4 mr-1" />
                                Reussi
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-red-700 text-sm">
                                <XCircleIcon className="w-4 h-4 mr-1" />
                                Echec
                              </span>
                            )}
                          </td>
                          <td>
                            {hasErreurs && (
                              <button
                                onClick={() => setExpandedId(isExpanded ? null : log.id)}
                                className="flex items-center text-sm text-primary-600 hover:text-primary-700"
                              >
                                {log.erreurs?.length} erreur(s)
                                {isExpanded ? (
                                  <ChevronUpIcon className="w-4 h-4 ml-1" />
                                ) : (
                                  <ChevronDownIcon className="w-4 h-4 ml-1" />
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={9} className="bg-gray-50 px-6 py-3">
                              {log.messageGlobal && (
                                <p className="text-sm font-medium text-gray-700 mb-2">
                                  {log.messageGlobal}
                                </p>
                              )}
                              <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                                {log.erreurs?.map((err, i) => (
                                  <li key={i}>{err}</li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {data && data.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} sur {data.pages} ({data.total} resultats)
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
              >
                Precedent
              </button>
              <button
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={page === data.pages}
                className="btn-secondary"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImportLogsPage;
